import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { Radio, Save, RefreshCw, Trash2, Play, Eye, PlusCircle, ExternalLink, Info, CheckCircle2, AlertTriangle, Loader2, MessageSquare, FileText, ChevronDown, ChevronRight, Clock, StopCircle, BarChart3, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/datetime';

/**
 * WhatsApp Group Broadcast — Compset daily report broadcaster (iter226).
 * Independent of existing WhatsApp Inbox / Guest Journey / Templates.
 * Phase 1 wires up Google-Sheets → screenshot → queue. Phase 2 adds the
 * external WhatsApp Bridge integration.
 */
export default function WAGroupBroadcast() {
  const [tab, setTab] = useState('setup');   // setup | mappings | announcement | deliveries | polls
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="wa-group-broadcast-page">
      <div className="mb-4 flex items-center gap-2">
        <Radio className="text-emerald-600" size={22} />
        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Group Broadcast</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6 max-w-3xl">
        Daily Compset Reports pulled from your Google Sheet and delivered to each
        hotel&apos;s WhatsApp Group via the external Bridge. Fully independent from the
        CRM Inbox, Guest Journey and Templates.
      </p>
      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {['setup', 'mappings', 'announcement', 'deliveries', 'polls'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-800'}`}
            data-testid={`wagb-tab-${t}`}
          >
            {t === 'polls' ? 'Poll Responses' : (t.charAt(0).toUpperCase() + t.slice(1))}
          </button>
        ))}
      </div>
      {tab === 'setup' && <SetupTab />}
      {tab === 'mappings' && <MappingsTab />}
      {tab === 'announcement' && <AnnouncementTab />}
      {tab === 'deliveries' && <DeliveriesTab />}
      {tab === 'polls' && <PollResponsesTab />}
    </div>
  );
}

function SetupTab() {
  const [s, setS] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, spreadsheet_title, tabs, ...}

  useEffect(() => {
    api.get('/broadcasts/settings').then(r => setS(r.data || {}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        apps_script_url: (s.apps_script_url || '').trim(),
        scheduler_enabled: !!s.scheduler_enabled,
        scheduler_time_hhmm: s.scheduler_time_hhmm || '11:00',
        scheduler_tz: s.scheduler_tz || 'Asia/Kolkata',
        bridge_url: s.bridge_url || '',
        caption_template: s.caption_template,
        caption_template_non_compset: s.caption_template_non_compset,
        inter_group_delay_seconds: Number.isFinite(Number(s.inter_group_delay_seconds)) ? Number(s.inter_group_delay_seconds) : 10,
      };
      if (s.bridge_bearer_token && s.bridge_bearer_token !== '***configured***') {
        body.bridge_bearer_token = s.bridge_bearer_token;
      }
      await api.put('/broadcasts/settings', body);
      toast.success('Settings saved.');
      const r = await api.get('/broadcasts/settings'); setS(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const testConnect = async () => {
    // Ensure the URL is persisted first so /test-connect uses the freshest value.
    if ((s.apps_script_url || '').trim() && s.apps_script_url !== s._original_url) {
      await save();
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/broadcasts/gsheet/test-connect');
      setTestResult(r.data);
      toast.success(`Connected to "${r.data.spreadsheet_title}" — ${r.data.tabs?.length || 0} worksheets found.`);
      // Refresh settings to pick up last_connected_at + connection_status.
      const rs = await api.get('/broadcasts/settings'); setS(rs.data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || String(e) });
      toast.error(e?.response?.data?.detail || 'Test Connect failed.');
    }
    finally { setTesting(false); }
  };

  const status = s.apps_script_connection_status;
  const statusPill = (() => {
    if (status === 'connected') return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Connected' };
    if (status === 'failed') return { cls: 'bg-red-50 text-red-700 border-red-200', label: 'Failed' };
    return { cls: 'bg-gray-50 text-gray-700 border-gray-200', label: 'Not tested' };
  })();

  return (
    <div className="space-y-6" data-testid="wagb-setup">
      <Card title="Google Sheets connection (via Apps Script)" icon={<ExternalLink size={16} />}>
        <p className="text-xs text-gray-600 mb-3">
          Paste the URL of your deployed Google Apps Script Web App. It runs as your own Google account and reads the spreadsheet
          on the CRM&apos;s behalf — no Service Account, GCP project or OAuth setup needed. See
          <code className="mx-1 px-1 bg-gray-100 rounded">/app/docs/apps-script-web-app.gs</code>
          for the ready-made script and one-page deployment guide.
        </p>
        <Field label="Google Apps Script Web App URL">
          <input
            className="input font-mono text-xs"
            data-testid="wagb-apps-script-url"
            placeholder="https://script.google.com/macros/s/AKfy…/exec"
            value={s.apps_script_url || ''}
            onChange={e => setS(v => ({ ...v, apps_script_url: e.target.value }))}
          />
        </Field>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="wagb-save-settings">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
          </button>
          <button
            onClick={testConnect}
            disabled={testing || !(s.apps_script_url || '').trim()}
            className="btn-secondary"
            data-testid="wagb-test-connect"
          >
            {testing ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Test Connect
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Status</div>
            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold border ${statusPill.cls}`} data-testid="wagb-apps-script-status">
              {statusPill.label}
            </span>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Spreadsheet</div>
            <div className="truncate" data-testid="wagb-apps-script-title">
              {s.apps_script_spreadsheet_title || '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Last connected</div>
            <div className="font-mono text-xs" data-testid="wagb-apps-script-last-connected">
              {s.apps_script_last_connected_at
                ? new Date(s.apps_script_last_connected_at).toLocaleString()
                : '—'}
            </div>
          </div>
        </div>
        {s.apps_script_last_error && (
          <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1">
            <AlertTriangle size={12} className="mt-0.5" /> {s.apps_script_last_error}
          </p>
        )}
        {testResult?.ok && (testResult.tabs?.length || 0) > 0 && (
          <p className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="wagb-apps-script-tabs-preview">
            Found <strong>{testResult.tabs.length}</strong> worksheets — the first few are:{' '}
            {testResult.tabs.slice(0, 6).map(t => t.name).join(', ')}
            {testResult.tabs.length > 6 ? '…' : ''}
          </p>
        )}
      </Card>

      <Card title="Delivery Options" icon={<Clock size={16} />}>
        <p className="text-xs text-gray-600 mb-3">
          The daily fire time is now controlled from the <strong>Broadcast Schedule</strong> card on the Mappings tab.
          Only the inter-group delay is configured here.
          <span className="text-gray-400 ml-2">(Timezone: Asia/Kolkata)</span>
        </p>
        <DelayPicker
          value={s.inter_group_delay_seconds ?? 10}
          onChange={(n) => setS(v => ({ ...v, inter_group_delay_seconds: n }))}
        />
      </Card>

      <BroadcastTemplateCard s={s} setS={setS} save={save} saving={saving} />

      <Card title="WhatsApp Bridge (Phase 2)" icon={<Radio size={16} />}>
        <p className="text-xs text-amber-700 flex items-start gap-1 mb-3">
          <Info size={12} className="mt-0.5" /> The Bridge is a separate Node.js service you deploy on your own VM. Leave blank until it&apos;s up — sends will queue and be visible in Deliveries as <code>screenshot_ok</code>.
        </p>
        <Field label="Bridge URL">
          <input className="input" placeholder="https://wa-bridge.example.com"
            value={s.bridge_url || ''}
            onChange={e => setS(v => ({ ...v, bridge_url: e.target.value }))}
            data-testid="wagb-bridge-url" />
        </Field>
        <Field label="Bridge Bearer Token">
          <input className="input" type="password" placeholder={s.bridge_bearer_token || 'paste token…'}
            onChange={e => setS(v => ({ ...v, bridge_bearer_token: e.target.value }))}
            data-testid="wagb-bridge-token" />
        </Field>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="wagb-save-bridge">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Bridge Settings
        </button>
      </Card>

      <BridgeStatusCard />
    </div>
  );
}

function BridgeStatusCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [refreshingQr, setRefreshingQr] = useState(false);
  const timer = useRef(null);
  const lastQrObjectUrl = useRef(null);

  const fetchQr = async () => {
    try {
      const r = await api.get('/broadcasts/wa/qr', { responseType: 'blob' });
      if (lastQrObjectUrl.current) URL.revokeObjectURL(lastQrObjectUrl.current);
      const url = URL.createObjectURL(r.data);
      lastQrObjectUrl.current = url;
      setQrUrl(url);
    } catch (e) {
      setQrUrl(null);
    }
  };
  const load = async () => {
    try {
      const r = await api.get('/broadcasts/wa/status');
      setSt(r.data || {});
      if (r.data?.qr_available) {
        // Only refetch QR if we don't already have one, or on demand.
        if (!lastQrObjectUrl.current) fetchQr();
      } else {
        if (lastQrObjectUrl.current) URL.revokeObjectURL(lastQrObjectUrl.current);
        lastQrObjectUrl.current = null;
        setQrUrl(null);
      }
    } catch (e) {
      setSt({ ok: false, session_state: 'unreachable', error: String(e?.message || e) });
    }
  };
  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    // Auto-refresh the QR itself every 20s while pairing (WA rotates the code).
    const qrTimer = setInterval(() => {
      if (st?.session_state === 'qr' && lastQrObjectUrl.current) fetchQr();
    }, 20000);
    return () => {
      clearInterval(timer.current);
      clearInterval(qrTimer);
      if (lastQrObjectUrl.current) URL.revokeObjectURL(lastQrObjectUrl.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.session_state]);
  const connect = async () => {
    setBusy(true);
    try {
      await api.post('/broadcasts/wa/connect');
      toast.success('Bridge connect requested. Watch for QR below.');
      setTimeout(load, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Connect failed.'); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(
      'Disconnect this WhatsApp session?\n\n' +
      'The current WhatsApp session will be destroyed and the local ' +
      'authentication data wiped. You can connect a new WhatsApp ' +
      'number afterward.\n\n' +
      'Your existing mappings, groups, delivery history and polls will ' +
      'NOT be deleted — only the WhatsApp connection is removed.'
    )) return;
    setBusy(true);
    try {
      await api.post('/broadcasts/wa/disconnect');
      toast.success('WhatsApp disconnected. Connect a new WhatsApp number.');
      // iter333 — Poll status aggressively for 10s so the UI reflects
      // the destroyed session immediately instead of waiting for the
      // next 10s poll tick.
      for (let i = 0; i < 5; i += 1) {
        await new Promise(r => setTimeout(r, 500));
        await load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Disconnect failed.'); }
    finally { setBusy(false); }
  };
  const refreshQr = async () => {
    setRefreshingQr(true);
    await fetchQr();
    setRefreshingQr(false);
  };
  const state = st?.session_state || '—';
  const stateClass = state === 'ready'
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : state === 'qr' ? 'text-amber-700 bg-amber-50 border-amber-200'
    : (state === 'unreachable' || state === 'not_configured' || state === 'auth_failed') ? 'text-red-700 bg-red-50 border-red-200'
    : 'text-gray-700 bg-gray-50 border-gray-200';
  return (
    <Card title="Bridge Status" icon={<Radio size={16} />} right={
      <div className="flex gap-2">
        <button onClick={load} className="btn-secondary" data-testid="wagb-bridge-refresh"><RefreshCw size={14} /></button>
        <button onClick={connect} disabled={busy} className="btn-primary" data-testid="wagb-bridge-connect">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Connect
        </button>
        <button onClick={disconnect} disabled={busy || state === 'stopped' || state === 'not_configured'} className="btn-secondary" data-testid="wagb-bridge-disconnect">
          Disconnect
        </button>
      </div>
    }>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-500 mb-0.5">State</div>
          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold border ${stateClass}`} data-testid="wagb-bridge-state">{state}</span>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Number</div>
          <div className="font-mono text-xs">{st?.connected_number || '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Groups</div>
          <div>{st?.groups_count ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Last seen</div>
          <div className="font-mono text-xs">{st?.last_seen ? new Date(st.last_seen).toLocaleTimeString() : '—'}</div>
        </div>
      </div>
      {st?.error && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1">
          <AlertTriangle size={12} className="mt-0.5" /> {st.error}
        </p>
      )}
      {qrUrl && (
        <div className="mt-4 p-4 border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg flex items-start gap-4">
          <img src={qrUrl} alt="QR" width={220} height={220} className="rounded bg-white p-2" data-testid="wagb-bridge-qr" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold mb-1">Scan this QR with your dedicated WhatsApp Business number</p>
            <ol className="list-decimal ml-4 space-y-0.5">
              <li>Open WhatsApp → Settings → Linked Devices</li>
              <li>Tap <strong>Link a Device</strong> and scan the code</li>
              <li>State will change to <code>ready</code> automatically</li>
            </ol>
            <button onClick={refreshQr} disabled={refreshingQr} className="mt-3 btn-secondary text-xs">
              {refreshingQr ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh QR
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function MappingsTab() {
  const [rows, setRows] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [waGroups, setWaGroups] = useState([]);
  const [waLoading, setWaLoading] = useState(false);
  // iter331b — Mapping summary counts rendered in the "Mapping Summary"
  // strip above the table. Live from `/broadcasts/mappings/summary`
  // so Admin never sees stale numbers after an add/delete.
  const [summary, setSummary] = useState({
    total: 0, compset: 0, text_only: 0, unmapped_properties: 0,
  });
  const loadSummary = async () => {
    try {
      const r = await api.get('/broadcasts/mappings/summary');
      const d = r?.data || {};
      setSummary({
        total: Number(d.total) || 0,
        compset: Number(d.compset) || 0,
        text_only: Number(d.text_only) || 0,
        unmapped_properties: Number(d.unmapped_properties) || 0,
      });
    } catch { /* silent */ }
  };
  // iter331 — Scope info surfaced from `/broadcasts/wa/groups`. Lets the
  // Mapping screen tell the operator whether BOTH admin and non-admin
  // (member) groups are listed, or whether only admin groups are
  // reachable (fallback / Bridge unavailable). The product spec from
  // iter331 asks for both the positive banner AND the admin-only
  // fallback copy to render verbatim depending on the API response.
  const [waScope, setWaScope] = useState({
    supports_non_admin: null,  // null = unknown, true/false once loaded
    info_message: '',
    fallback_message: '',
    source: '',
  });
  const [properties, setProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({ crm_hotel_id: '', crm_hotel_name: '', gsheet_tab_name: '', gsheet_gid: null, wa_group_name: '', wa_group_id: '', ota_channel: 'Agoda' });
  // Iter236 — duplicate CRM property detection.
  // We compare by stable `crm_hotel_id` (preferred) and by case-insensitive
  // `crm_hotel_name` (fallback for legacy mappings created without an id).
  const duplicateOf = useMemo(() => {
    if (!form.crm_hotel_id && !form.crm_hotel_name) return null;
    const nameL = (form.crm_hotel_name || '').trim().toLowerCase();
    return rows.find(m => {
      if (form.crm_hotel_id && m.crm_hotel_id === form.crm_hotel_id) return true;
      if (nameL && (m.crm_hotel_name || '').trim().toLowerCase() === nameL) return true;
      return false;
    }) || null;
  }, [rows, form.crm_hotel_id, form.crm_hotel_name]);

  // iter265 — Already-mapped items must disappear from the "Add Mapping"
  // dropdowns so they can't be picked twice. We build two sets from the
  // current mappings list:
  //   • mappedPropertyKeys — union of crm_hotel_id + lowercased
  //     crm_hotel_name so legacy mappings without an id are also
  //     filtered out.
  //   • mappedTabNames — lowercased worksheet names. Text-Only
  //     mappings (empty gsheet_tab_name) do NOT consume a worksheet.
  const mappedPropertyKeys = useMemo(() => {
    const ids = new Set();
    const names = new Set();
    for (const m of rows) {
      if (m.crm_hotel_id) ids.add(m.crm_hotel_id);
      const n = (m.crm_hotel_name || '').trim().toLowerCase();
      if (n) names.add(n);
    }
    return { ids, names };
  }, [rows]);
  const mappedTabNames = useMemo(() => {
    const s = new Set();
    for (const m of rows) {
      const t = (m.gsheet_tab_name || '').trim().toLowerCase();
      if (t) s.add(t);
    }
    return s;
  }, [rows]);
  // Filtered lists passed to the pickers. If the user has ALREADY chosen
  // an item in the "add" form we keep it visible (so it renders as the
  // current selection) even though it wouldn't yet be in `rows`.
  const availableProperties = useMemo(() => properties.filter(p => {
    if (form.crm_hotel_id && p.id === form.crm_hotel_id) return true;
    if (p.id && mappedPropertyKeys.ids.has(p.id)) return false;
    const nameL = (p.name || '').trim().toLowerCase();
    if (nameL && mappedPropertyKeys.names.has(nameL)) return false;
    return true;
  }), [properties, mappedPropertyKeys, form.crm_hotel_id]);
  const availableTabs = useMemo(() => tabs.filter(t => {
    const nameL = (t.name || '').trim().toLowerCase();
    if (!nameL) return false;
    // Keep the currently-selected worksheet visible in the picker so its
    // label still shows before the user submits.
    if (form.gsheet_tab_name && nameL === form.gsheet_tab_name.trim().toLowerCase()) return true;
    return !mappedTabNames.has(nameL);
  }), [tabs, mappedTabNames, form.gsheet_tab_name]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Iter237 — global schedule settings (persisted in broadcast_settings).
  const [sched, setSched] = useState({
    global_report_date: '', global_send_date: '', global_send_time: '',
    auto_send: false, last_scheduler_fire_key: '', last_scheduler_fired_at: '',
  });
  const [savingSched, setSavingSched] = useState(false);
  const loadSched = async () => {
    try {
      const r = await api.get('/broadcasts/settings');
      setSched(s => ({
        ...s,
        global_report_date: r.data?.global_report_date || '',
        global_send_date: r.data?.global_send_date || '',
        global_send_time: r.data?.global_send_time || '',
        auto_send: !!r.data?.auto_send,
        last_scheduler_fire_key: r.data?.last_scheduler_fire_key || '',
        last_scheduler_fired_at: r.data?.last_scheduler_fired_at || '',
      }));
    } catch (_e) { /* settings may not exist yet */ }
  };
  const saveSched = async (patch) => {
    setSavingSched(true);
    try {
      const next = { ...sched, ...patch };
      // Send only the schedule keys — do not touch bridge_url/token/etc.
      await api.put('/broadcasts/settings', {
        global_report_date: next.global_report_date || null,
        global_send_date: next.global_send_date || null,
        global_send_time: next.global_send_time || null,
        auto_send: !!next.auto_send,
      });
      setSched(next);
      toast.success('Schedule updated.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
    finally { setSavingSched(false); }
  };
  useEffect(() => { loadSched(); }, []);
  const load = async () => {
    const r = await api.get('/broadcasts/mappings', { params: { search: q } });
    setRows(r.data.data || []);
  };
  const loadWaGroups = async () => {
    setWaLoading(true);
    try {
      const r = await api.get('/broadcasts/wa/groups');
      if (r.data?.ok) {
        setWaGroups(r.data.data || []);
        setWaScope({
          supports_non_admin: r.data.supports_non_admin !== false,
          info_message: r.data.info_message || '',
          fallback_message: r.data.fallback_message || '',
          source: r.data.source || 'bridge_wwebjs',
        });
      } else {
        setWaScope({
          supports_non_admin: r.data?.supports_non_admin === true,
          info_message: '',
          fallback_message: r.data?.fallback_message || '',
          source: r.data?.source || 'unknown',
        });
        if (r.data?.error) toast.error(`WA groups: ${r.data.error}`);
      }
    } catch (e) { /* Bridge may not be configured yet — silent. */ }
    finally { setWaLoading(false); }
  };
  const loadProperties = async (q = '') => {
    setPropsLoading(true);
    try {
      // iter332 — Unmapped-only, deduped, full-list, exact-status.
      // Backend `/broadcasts/properties/unmapped` already filters out
      // mapped properties AND dedupes by GRB id, so the dropdown never
      // repeats the same physical hotel and never shows one that's
      // already in the Mappings table.
      const r = await api.get('/broadcasts/properties/unmapped',
                              { params: q ? { search: q } : {} });
      const list = (r?.data?.data || []).map(p => ({
        id: p?.id,
        name: p?.name || '(unnamed)',
        grb: p?.grb || '',
        city: p?.city || '',
        status: p?.status || 'Active',
      })).filter(p => !!p.id);
      setProperties(list);
    } catch (e) {
      // Fall back to the legacy lightweight list so Admin still sees
      // *something* while the new endpoint rolls out.
      try {
        const r = await api.get('/properties', { params: { lightweight: true } });
        const list = (r?.data?.data || []).map(p => ({
          id: p?.id,
          name: p?.propertyName || p?.name || '(unnamed)',
          grb: p?.grb_property_id || '',
          city: p?.city || '',
          status: p?.propertyStatus || 'Active',
        })).filter(p => !!p.id).sort((a, b) => a.name.localeCompare(b.name));
        setProperties(list);
      } catch { toast.error('Failed to load properties.'); }
    }
    finally { setPropsLoading(false); }
  };
  useEffect(() => { load(); loadSummary(); }, [q]); // eslint-disable-line
  useEffect(() => {
    api.get('/broadcasts/gsheet/tabs').then(r => setTabs(r.data.tabs || [])).catch(() => {});
    loadWaGroups();
    loadProperties();
  }, []);
  const submit = async () => {
    if (!form.crm_hotel_name || !form.wa_group_name) {
      toast.error('CRM Hotel and WhatsApp Group are required.');
      return;
    }
    // Iter236 — frontend guard. Backend enforces the same rule.
    if (duplicateOf) {
      toast.error(`${form.crm_hotel_name} is already mapped.`);
      return;
    }
    setSaving(true);
    try {
      // Iter238 — gsheet_tab_name is optional. Empty/sentinel = Text-Only mapping.
      const tabName = (form.gsheet_tab_name || '').trim();
      const isTextOnly = tabName === '' || tabName === '__none__';
      const chosenTab = isTextOnly ? null : tabs.find(t => t.name === tabName);
      await api.post('/broadcasts/mappings', {
        crm_hotel_id: form.crm_hotel_id || null,
        crm_hotel_name: form.crm_hotel_name,
        gsheet_tab_name: isTextOnly ? '' : tabName,
        gsheet_gid: chosenTab?.gid ?? null,
        wa_groups: [{ id: form.wa_group_id, name: form.wa_group_name }],
        ota_channel: form.ota_channel || 'Agoda',
      });
      toast.success(isTextOnly ? 'Text-Only mapping added.' : 'CompSet mapping added.');
      setForm({ crm_hotel_id: '', crm_hotel_name: '', gsheet_tab_name: '', gsheet_gid: null, wa_group_name: '', wa_group_id: '', ota_channel: 'Agoda' });
      load();
      loadSummary();
      loadProperties(); // iter332 — refresh dropdown so just-mapped property disappears
    } catch (e) {
      // Iter236 — surface the structured 409 payload from the backend.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object') {
        toast.error(detail.message || 'Property already mapped.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Save failed.');
      }
    }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    await api.delete(`/broadcasts/mappings/${id}`);
    toast.success('Deleted.');
    load();
    loadSummary();
    loadProperties(); // iter332 — refresh dropdown so unmapped property reappears
  };

  // Iter240 — bulk selection state. Selection lives keyed by mapping ID so
  // it survives search/filter changes without losing selection accidentally.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // iter273 — Run mode picker shared by Run Now + Run Selected.
  //   'all'       → every mapping using its own broadcast_type (default)
  //   'only_text' → force text-only across ALL mappings (skip screenshots
  //                 even for compset mappings)
  //   'compset'   → only mappings that are compset (have a worksheet)
  const [runMode, setRunMode] = useState('all');
  // Only selection IDs actually present in the current (possibly filtered)
  // rows count towards "selected". That way Select All + search behave the
  // way users expect (see spec: search then Select All → only filtered rows).
  const visibleSelectedIds = useMemo(
    () => rows.filter(r => selectedIds.has(r.id)).map(r => r.id),
    [rows, selectedIds]
  );
  const selectedCount = visibleSelectedIds.length;
  const allVisibleSelected = rows.length > 0 && visibleSelectedIds.length === rows.length;
  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        // Deselect only the visible ones (leaves hidden selections alone —
        // safer if the user changed the filter mid-way).
        const next = new Set(prev);
        rows.forEach(r => next.delete(r.id));
        return next;
      }
      const next = new Set(prev);
      rows.forEach(r => next.add(r.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    if (visibleSelectedIds.length === 0) return;
    if (!window.confirm(`Delete ${visibleSelectedIds.length} selected mapping${visibleSelectedIds.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post('/broadcasts/mappings/bulk-delete', { mapping_ids: visibleSelectedIds });
      toast.success(`Deleted ${r.data?.deleted ?? visibleSelectedIds.length} mapping(s).`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed.');
    } finally { setBulkBusy(false); }
  };
  const runSelected = async () => {
    if (visibleSelectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const rd = sched.global_report_date || null;
      const r = await api.post('/broadcasts/run-selected', {
        mapping_ids: visibleSelectedIds,
        mode: runMode,
        ...(rd ? { report_date: rd } : {}),
      });
      // iter339 — surface the specific reason instead of a silent "Queued 0".
      const queued = r.data?.queued ?? 0;
      if (queued === 0) {
        const msg = r.data?.message
          || 'Nothing was queued. Mappings may already be sent/skipped for this Report Date, or the Report Date filter does not match. Change the Report Date or click Retry Pending.';
        toast.error(msg);
      } else {
        toast.success(`Queued ${queued} job(s). Watch Live Progress.`);
      }
      clearSelection();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.error === 'unknown_mapping_ids'
          ? `Some mappings no longer exist: ${(detail.missing || []).join(', ')}`
          : 'Run Selected failed.';
      toast.error(msg);
    } finally { setBulkBusy(false); }
  };

  const preview = async (id) => {
    setPreviewing(id);
    try {
      const r = await api.post(`/broadcasts/preview/${id}`, null, { responseType: 'blob' });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed.'); }
    finally { setPreviewing(null); }
  };
  return (
    <div className="space-y-6" data-testid="wagb-mappings">
      {/* iter331 — WhatsApp API scope banner. Confirms to the operator
          whether BOTH admin + member groups are available (Bridge path,
          the normal case) or whether the fallback admin-only limit
          applies (Cloud API fallback / Bridge unavailable). The exact
          fallback copy is required verbatim by the product spec. */}
      {waScope.supports_non_admin === true && waScope.info_message && (
        <div
          className="flex items-start gap-2 text-sm px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800"
          data-testid="wagb-wa-scope-banner-ok"
        >
          <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
          <span>{waScope.info_message}</span>
        </div>
      )}
      {waScope.supports_non_admin === false && (
        <div
          className="flex items-start gap-2 text-sm px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800"
          data-testid="wagb-wa-scope-banner-fallback"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            {waScope.fallback_message ||
              'Only groups where you are the admin are listed here. To add other groups, make this number an admin first.'}
          </span>
        </div>
      )}
      <Card title="Add Mapping" icon={<PlusCircle size={16} />}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="CRM Hotel">
            <PropertyCombobox
              properties={availableProperties}
              loading={propsLoading}
              value={form.crm_hotel_id}
              onChange={(p) => setForm(f => ({
                ...f,
                crm_hotel_id: p?.id || '',
                crm_hotel_name: p?.name || '',
              }))}
              onRefresh={loadProperties}
            />
          </Field>
          <Field label="Google Sheet Worksheet (optional — leave for Text Only)">
            <SearchableCombobox
              items={[
                { id: '__none__', name: '— No CompSet / Text Only —' },
                ...availableTabs.map(t => ({ id: t.name, name: t.name })),
              ]}
              value={form.gsheet_tab_name === '' ? '__none__' : (form.gsheet_tab_name || '__none__')}
              matchBy="id"
              placeholder="Search worksheets or pick Text Only…"
              emptyLabel="No worksheets loaded"
              testIdPrefix="wagb-map-tab"
              onChange={item => setForm(f => ({
                ...f,
                gsheet_tab_name: (item?.id === '__none__') ? '' : (item?.name || ''),
              }))}
            />
          </Field>
          <Field label="WhatsApp Group">
            <div className="flex gap-1">
              <div className="flex-1">
                <SearchableCombobox
                  items={waGroups}
                  value={form.wa_group_id}
                  matchBy="id"
                  placeholder={waGroups.length ? `Search groups (${waGroups.length})…` : 'No groups loaded'}
                  emptyLabel="No groups loaded — click Refresh"
                  testIdPrefix="wagb-map-group"
                  onChange={g => setForm(f => ({ ...f, wa_group_id: g?.id || '', wa_group_name: g?.name || '' }))}
                />
              </div>
              <button type="button" onClick={loadWaGroups} className="btn-secondary !px-2" title="Refresh groups from Bridge" data-testid="wagb-map-refresh-groups">
                {waLoading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
              </button>
            </div>
            {waGroups.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                No groups yet — make sure the Bridge state is <code>ready</code> (Setup tab), then click refresh.
              </p>
            )}
          </Field>
          <Field label="Group ID (auto-filled)">
            <input readOnly className="input font-mono text-xs bg-gray-50" placeholder="120…@g.us" value={form.wa_group_id}
              data-testid="wagb-map-group-id" />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <Field label="Channel / OTA">
            <select className="input" value={form.ota_channel || 'Agoda'}
              onChange={e => setForm(f => ({ ...f, ota_channel: e.target.value }))}
              data-testid="wagb-map-channel">
              {['Agoda', 'Booking.com', 'MakeMyTrip', 'Goibibo', 'Expedia', 'Other'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button onClick={submit} disabled={saving || !!duplicateOf} className="btn-primary" data-testid="wagb-map-save">
            <Save size={14} /> Add Mapping
          </button>
          {duplicateOf && (
            <div
              className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700"
              data-testid="wagb-map-dup-warning"
            >
              <AlertTriangle size={14} />
              <span>
                <strong>{form.crm_hotel_name}</strong> is already mapped
                {(duplicateOf.wa_groups && duplicateOf.wa_groups[0]?.name)
                  ? <> to <em>{duplicateOf.wa_groups[0].name}</em></>
                  : null}
                .
              </span>
            </div>
          )}
        </div>
      </Card>

      <LiveProgress />

      {/* Iter237 — Global Broadcast Schedule (source of truth for auto-fire) */}
      <Card title="Broadcast Schedule" icon={<Clock size={16} />} right={
        <label className="inline-flex items-center gap-2 text-sm select-none" data-testid="wagb-sched-auto-send-wrap">
          <span className={`font-semibold ${sched.auto_send ? 'text-emerald-700' : 'text-gray-500'}`}>
            Auto Send {sched.auto_send ? 'ON' : 'OFF'}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={sched.auto_send}
            data-testid="wagb-sched-auto-send"
            onClick={() => saveSched({ auto_send: !sched.auto_send })}
            disabled={savingSched}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${sched.auto_send ? 'bg-emerald-600' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform mt-0.5 ${sched.auto_send ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </label>
      }>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Report Date (CompSet data date)">
            <input type="date" className="input" value={sched.global_report_date}
              onChange={e => setSched(s => ({ ...s, global_report_date: e.target.value }))}
              onBlur={e => saveSched({ global_report_date: e.target.value })}
              data-testid="wagb-sched-report-date" />
          </Field>
          <Field label="Send Date (WhatsApp delivery date)">
            <input type="date" className="input" value={sched.global_send_date}
              onChange={e => setSched(s => ({ ...s, global_send_date: e.target.value }))}
              onBlur={e => saveSched({ global_send_date: e.target.value })}
              data-testid="wagb-sched-send-date" />
          </Field>
          <Field label="Send Time (Asia/Kolkata)">
            <input type="time" className="input" value={sched.global_send_time}
              onChange={e => setSched(s => ({ ...s, global_send_time: e.target.value }))}
              onBlur={e => saveSched({ global_send_time: e.target.value })}
              data-testid="wagb-sched-send-time" />
          </Field>
        </div>
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
          These dates apply to <strong>all active mappings</strong>. On the configured
          Send Date at the Send Time (IST), the backend fetches CompSet data for the
          Report Date and broadcasts to every mapped group.
          {sched.last_scheduler_fired_at && (
            <> Last auto-fire: <span className="font-mono">{sched.last_scheduler_fire_key}</span> ({new Date(sched.last_scheduler_fired_at).toLocaleString()}).</>
          )}
        </p>
      </Card>

      {/* iter331b — Mapping Summary strip. Pulls dynamic counts from
          /broadcasts/mappings/summary; refreshes after add/delete. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wagb-mapping-summary">
        <SummaryCard
          testid="wagb-sum-total"
          label="Total Mappings"
          value={summary.total}
          tone="emerald"
        />
        <SummaryCard
          testid="wagb-sum-compset"
          label="CompSet Mappings"
          value={summary.compset}
          tone="sky"
        />
        <SummaryCard
          testid="wagb-sum-text"
          label="Text Only Mappings"
          value={summary.text_only}
          tone="violet"
        />
        <SummaryCard
          testid="wagb-sum-unmapped"
          label="Unmapped Properties"
          value={summary.unmapped_properties}
          tone="amber"
        />
      </div>

      <Card
        title="Mappings"
        icon={<Radio size={16} />}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <input className="input !py-1 !text-sm w-56" placeholder="Search…"
              value={q} onChange={e => setQ(e.target.value)} data-testid="wagb-map-search" />
            <button onClick={load} className="btn-secondary" data-testid="wagb-map-refresh"><RefreshCw size={14} /></button>
            {selectedCount > 0 && (
              <span className="text-xs font-semibold px-2 py-1 rounded bg-teal-50 text-teal-700 border border-teal-200"
                    data-testid="wagb-selected-count">
                {selectedCount} selected
              </span>
            )}
            <button
              onClick={bulkDelete}
              disabled={selectedCount === 0 || bulkBusy}
              className="btn-secondary !text-red-600 !border-red-200 hover:!bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wagb-delete-selected"
            >
              {bulkBusy ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Delete Selected
            </button>
            <button
              onClick={runSelected}
              disabled={selectedCount === 0 || bulkBusy}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wagb-run-selected"
            >
              {bulkBusy ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Run Selected
            </button>
            {/* iter273 — Run mode dropdown, shared by Run Now + Run Selected */}
            <select
              value={runMode}
              onChange={(e) => setRunMode(e.target.value)}
              className="input !py-1 !text-sm !w-auto"
              data-testid="wagb-run-mode"
              title="Which mappings + template to use when broadcasting"
            >
              <option value="all">All</option>
              <option value="only_text">Only Text</option>
              <option value="compset">Compset</option>
            </select>
            <button onClick={async () => {
                const rd = window.prompt('Report Date to fetch (YYYY-MM-DD). Leave blank to use the Global Report Date from Broadcast Schedule.', sched.global_report_date || '');
                try {
                  const r = await api.post('/broadcasts/run-now', {
                    mode: runMode,
                    ...(rd ? { report_date: rd } : {}),
                  });
                  // iter331b — honest resume messaging. `queued` is now
                  // the number of mappings actually eligible for this
                  // pass (previously-sent mappings are excluded by the
                  // backend). If 0, the UI tells the admin everything
                  // is already terminal.
                  const n = r.data?.queued ?? 0;
                  if (n === 0) {
                    // iter339 — surface the specific skip reason from
                    // the backend (sent/skipped/send_date_mismatch/…)
                    // instead of a one-size-fits-all message.
                    toast.success(r.data?.message
                      || 'All eligible mappings have already been processed.');
                  } else {
                    toast.success(`${n} mapping${n === 1 ? '' : 's'} queued — watch Live Progress above. Already-sent mappings were preserved.`);
                  }
                }
                catch (e) { toast.error(e?.response?.data?.detail || 'Start / Resume failed.'); }
              }} className="btn-primary" data-testid="wagb-run-now">
              <Play size={14} /> Start / Resume
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    disabled={rows.length === 0}
                    aria-label="Select all visible mappings"
                    className="w-4 h-4 accent-teal-600 cursor-pointer disabled:cursor-not-allowed"
                    data-testid="wagb-select-all"
                  />
                </th>
                <th className="text-left px-3 py-2">CRM Hotel</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Sheet Worksheet</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">WhatsApp Group</th>
                <th className="text-left px-3 py-2">Channel</th>
                <th className="text-left px-3 py-2">Report Date</th>
                <th className="text-left px-3 py-2">Send Date</th>
                <th className="text-center px-3 py-2 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="10" className="px-3 py-6 text-center text-gray-500">No mappings yet.</td></tr>}
              {rows.map(m => {
                const isTextOnly = !m.gsheet_tab_name || (m.broadcast_type === 'text_only');
                const isChecked = selectedIds.has(m.id);
                // iter332 — row status badge. Preserve the exact
                // status text from the live property record; map a
                // reasonable color for any of the known states and
                // fall back to a neutral chip for anything else.
                const pStatusRaw = (m.property_status || 'Active').toString();
                const pStatusLower = pStatusRaw.toLowerCase();
                const pStatusCls =
                  pStatusLower === 'active'       ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  pStatusLower === 'inactive'     ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  pStatusLower === 'discontinued' ? 'bg-red-50 text-red-700 border-red-200' :
                  pStatusLower === 'terminated'   ? 'bg-rose-50 text-rose-700 border-rose-200' :
                  /* fallback */                    'bg-gray-50 text-gray-600 border-gray-200';
                return (
                <tr key={m.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isChecked ? 'bg-teal-50/40' : ''}`} data-testid={`wagb-row-${m.id}`}>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleRow(m.id)}
                      aria-label={`Select ${m.crm_hotel_name}`}
                      className="w-4 h-4 accent-teal-600 cursor-pointer"
                      data-testid={`wagb-row-${m.id}-checkbox`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">{m.crm_hotel_name}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${pStatusCls}`}
                      data-testid={`wagb-row-${m.id}-status`}
                    >
                      {pStatusRaw}
                    </span>
                  </td>
                  <td className="px-3 py-2">{m.gsheet_tab_name || <span className="text-gray-400 italic">— none —</span>}</td>
                  <td className="px-3 py-2">
                    {isTextOnly
                      ? <span className="inline-block px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-xs font-medium" data-testid={`wagb-row-${m.id}-type`}>Text Only</span>
                      : <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium" data-testid={`wagb-row-${m.id}-type`}>CompSet</span>}
                  </td>
                  <td className="px-3 py-2">{(m.wa_groups || []).map(g => g.name).join(', ')}</td>
                  <td className="px-3 py-2">{m.ota_channel || 'Agoda'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{sched.global_report_date || <span className="text-gray-400">not set</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs">{sched.global_send_date || <span className="text-gray-400">not set</span>}</td>
                  <td className="px-3 py-2 text-center">
                    {!isTextOnly && (
                      <button onClick={() => preview(m.id)} className="text-teal-600 hover:text-teal-800 mr-2" title="Preview screenshot" data-testid={`wagb-preview-${m.id}`}>
                        {previewing === m.id ? <Loader2 className="animate-spin" size={14} /> : <Eye size={14} />}
                      </button>
                    )}
                    <button onClick={() => del(m.id)} className="text-red-600 hover:text-red-800" data-testid={`wagb-delete-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </Card>

      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-6" onClick={() => setPreviewUrl(null)}>
          <div className="bg-white rounded-xl max-w-4xl max-h-full overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <img src={previewUrl} alt="Preview" className="max-w-full" />
          </div>
        </div>
      )}
    </div>
  );
}

function LiveProgress() {
  const [run, setRun] = useState(null);
  const [now, setNow] = useState(Date.now());
  // Iter243 — Reset Run local state MUST be declared before any early
  // return to satisfy React Hooks rules.
  const [resetting, setResetting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get('/broadcasts/runs/current');
        if (!cancelled) setRun(r.data?.data || null);
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 2000);
    const clock = setInterval(() => setNow(Date.now()), 500);
    return () => { cancelled = true; clearInterval(t); clearInterval(clock); };
  }, []);
  if (!run) return null;
  const active = run.status === 'running';
  const total = run.total || 0;
  const sent = run.sent || 0;
  const failed = run.failed || 0;
  const pending = Math.max(0, run.pending ?? (total - sent - failed));
  // iter331b — expanded counters surfaced from `broadcast_runs`.
  const skipped = run.skipped || 0;
  const processing = run.processing || 0;
  const compsetSent = run.compset_sent || 0;
  const textSent = run.text_sent || 0;
  const remaining = run.remaining ?? Math.max(0, pending + processing);
  const pct = total > 0 ? Math.round(((sent + failed + skipped) / total) * 100) : 0;
  const waitLeft = run.wait_until
    ? Math.max(0, Math.ceil((run.wait_until * 1000 - now) / 1000)) : 0;
  const etaSec = Math.max(0, (run.eta_seconds || 0));
  const fmt = (s) => {
    if (!s) return '0s';
    const m = Math.floor(s / 60); const ss = s % 60;
    return m > 0 ? `${m}m ${ss}s` : `${ss}s`;
  };
  const statusPill = active
    ? { cls: 'bg-blue-50 text-blue-700 border-blue-200', label: run.phase || 'running' }
    : run.status === 'finished'
      ? { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'finished' }
      : run.status === 'reset'
        ? { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'reset' }
        : { cls: 'bg-gray-50 text-gray-700 border-gray-200', label: run.status || 'idle' };

  // iter332 — Two distinct actions, two distinct buttons.
  //
  //   RESET: hard-clears the Live Progress counters, stops the worker,
  //          but DOES NOT re-queue any job and DOES NOT auto-start a
  //          new run. The operator must click Start / Resume to begin
  //          again. Historical Deliveries remain intact.
  //
  //   RETRY: re-queues failed/blocked/canceled/stale-processing →
  //          pending and starts a fresh worker. `sent` + `skipped`
  //          are never touched. Shows "Nothing to retry" if the queue
  //          is already fully terminal.
  const canReset = !!run.id && !resetting && (active || run.status !== 'idle');
  const doReset = async () => {
    if (!canReset) return;
    if (!window.confirm(
      'Reset this broadcast run?\n\n' +
      'This will clear the current Live Progress and stop the worker. ' +
      'Previously delivered messages stay delivered — history in the ' +
      'Deliveries tab is preserved. Nothing new will be sent until you ' +
      'click Start / Resume.'
    )) return;
    setResetting(true);
    try {
      await api.post(`/broadcasts/runs/${run.id}/reset`);
      toast.success('Run reset. Live Progress cleared. Click Start / Resume when ready.');
      setRun(prev => prev ? {
        ...prev, status: 'reset', phase: 'reset',
        total: 0, sent: 0, failed: 0, pending: 0, processing: 0,
        skipped: 0, remaining: 0, compset_sent: 0, text_sent: 0,
        wait_until: null, eta_seconds: 0,
      } : prev);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Reset failed.'));
    } finally { setResetting(false); }
  };
  const canRetry = !!run.id && !retrying;
  const doRetry = async () => {
    if (!canRetry) return;
    if (!window.confirm(
      'Retry pending mappings?\n\n' +
      'Only Pending, Failed and stuck mappings will be retried. ' +
      'Successfully Sent mappings will NOT be sent again. Skipped ' +
      'mappings (Inactive / Discontinued / missing CompSet data) ' +
      'will remain Skipped.'
    )) return;
    setRetrying(true);
    try {
      const r = await api.post(`/broadcasts/runs/${run.id}/retry-pending`);
      const retried = r.data?.retried_jobs ?? 0;
      const eligible = r.data?.eligible_pending ?? 0;
      if (eligible === 0 && retried === 0) {
        toast.success('Nothing to retry. All eligible mappings have already been processed.');
      } else {
        toast.success(`${retried} re-queued (+${Math.max(0, eligible - retried)} already pending). Already-sent mappings preserved.`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Retry failed.'));
    } finally { setRetrying(false); }
  };

  return (
    <Card title="Live Progress" icon={<Clock size={16} />} data-testid="wagb-live-progress">
      <div className="flex items-center gap-3 mb-3">
        <span className={`px-2 py-1 rounded text-xs font-semibold border ${statusPill.cls}`} data-testid="wagb-live-status">
          {statusPill.label}
        </span>
        {active && (
          <span className="text-xs text-gray-600">
            {run.phase === 'waiting'
              ? <>Waiting <strong data-testid="wagb-live-wait">{waitLeft}s</strong> before next group…</>
              : <>Sending to <strong data-testid="wagb-live-current-hotel">{run.current_hotel_name || '—'}</strong>
                  <span className="ml-1 text-gray-500">
                    → group <strong data-testid="wagb-live-current-group">{run.current_group_name || '—'}</strong>
                  </span>
                </>
            }
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-500">
            ETA <strong data-testid="wagb-live-eta">{fmt(etaSec)}</strong>
          </span>
          {(active || resetting) && (
            <>
            <button
              onClick={doReset}
              disabled={!canReset}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wagb-reset-run"
              title="Stop the current broadcast run"
            >
              {resetting ? <Loader2 className="animate-spin" size={12} /> : <StopCircle size={12} />}
              Reset
            </button>
            <button
              type="button"
              onClick={doRetry}
              disabled={!canRetry}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg border border-amber-200 bg-white text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wagb-retry-pending"
              title="Retry only Pending / Failed mappings (never resend Sent)"
            >
              {retrying ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
              Retry Pending
            </button>
            </>
          )}
        </span>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="wagb-live-bar" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-7 gap-2 text-center text-sm">
        <Stat label="Total" val={total} testid="wagb-live-total" />
        <Stat label="Sent" val={sent} cls="text-emerald-700" testid="wagb-live-sent" />
        <Stat label="Failed" val={failed} cls="text-red-700" testid="wagb-live-failed" />
        <Stat label="Pending" val={pending} cls="text-amber-700" testid="wagb-live-pending" />
        <Stat label="Remaining" val={remaining} testid="wagb-live-remaining" />
        {/* iter331b — Breakdown of successful sends by delivery type. */}
        <Stat label="CompSet Sent" val={compsetSent} cls="text-sky-700" testid="wagb-live-compset-sent" />
        <Stat label="Text Sent" val={textSent} cls="text-violet-700" testid="wagb-live-text-sent" />
      </div>
      {skipped > 0 && (
        <div className="mt-2 text-xs text-gray-600" data-testid="wagb-live-skipped">
          <span className="font-medium">{skipped}</span> mapping{skipped === 1 ? '' : 's'} skipped
          <span className="text-gray-400"> (Inactive / Discontinued / no CompSet data)</span>
        </div>
      )}
    </Card>
  );
}
function Stat({ label, val, cls = '', testid }) {
  return (
    <div className="p-2 bg-gray-50 rounded" data-testid={testid}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{val}</div>
    </div>
  );
}

/** iter331b — Mapping-summary chip card used above the Mappings table. */
function SummaryCard({ label, value, tone = 'emerald', testid }) {
  const toneCls = {
    emerald: 'from-emerald-50 to-emerald-100/60 border-emerald-200 text-emerald-800',
    sky:     'from-sky-50 to-sky-100/60 border-sky-200 text-sky-800',
    violet:  'from-violet-50 to-violet-100/60 border-violet-200 text-violet-800',
    amber:   'from-amber-50 to-amber-100/60 border-amber-200 text-amber-800',
  }[tone] || 'from-gray-50 to-gray-100/60 border-gray-200 text-gray-800';
  return (
    <div
      className={`relative overflow-hidden rounded-lg border bg-gradient-to-br ${toneCls} px-4 py-3`}
      data-testid={testid}
    >
      <div className="text-xs font-medium opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1" data-testid={`${testid}-value`}>{value}</div>
    </div>
  );
}

function DeliveriesTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/broadcasts/jobs', { params: { report_date: date, status: status || undefined, limit: 200 } });
      setRows(r.data.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date, status]); // eslint-disable-line
  const retry = async () => { await api.post('/broadcasts/retry-failed'); toast.success('Retry queued.'); load(); };
  return (
    <div className="space-y-4" data-testid="wagb-deliveries">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" className="input !py-1 !text-sm" value={date} onChange={e => setDate(e.target.value)} data-testid="wagb-log-date" />
        <select className="input !py-1 !text-sm" value={status} onChange={e => setStatus(e.target.value)} data-testid="wagb-log-status">
          <option value="">All statuses</option>
          {['pending','screenshotting','screenshot_ok','sending','sent','failed','skipped','blocked'].map(s => <option key={s}>{s}</option>)}
        </select>
        <button onClick={load} className="btn-secondary"><RefreshCw size={14} /></button>
        <button onClick={retry} className="btn-primary"><RefreshCw size={14} /> Retry Failed</button>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-8"></th>
              <th className="text-left px-3 py-2">Hotel</th>
              <th className="text-left px-3 py-2">Sheet Tab</th>
              <th className="text-left px-3 py-2">Group</th>
              <th className="text-left px-3 py-2">Report Date</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-center px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Attempts</th>
              <th className="text-left px-3 py-2">Sent At</th>
              <th className="text-left px-3 py-2">Reason / Error</th>
              <th className="text-center px-3 py-2">Screenshot</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="11" className="p-4 text-center text-gray-500"><Loader2 className="animate-spin inline mr-1" size={14} /> loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="11" className="p-4 text-center text-gray-500">No deliveries.</td></tr>}
            {rows.map(r => (
              <DeliveryRow key={r.id} r={r} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ErrorPanel({ raw }) {
  // The CRM stashes Apps Script's full JSON body ({ok:false,error,details}) as
  // `skip_reason` / `wa_error` when it's parseable. Otherwise the raw string
  // is displayed as-is so the operator always sees exact failure diagnostics.
  let title = 'Delivery error';
  let message = raw;
  let details = null;
  try {
    // The stored value looks like:
    //   'apps_script_screenshot_failed: {"ok":false,"error":"…","details":{…}}'
    // Peel off the prefix and JSON.parse the rest.
    const jsonStart = raw.indexOf('{');
    if (jsonStart !== -1) {
      const parsed = JSON.parse(raw.slice(jsonStart));
      if (parsed && parsed.error) {
        title = parsed.error;
        message = null;
        details = parsed.details || null;
      }
    }
  } catch { /* keep raw */ }
  return (
    <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800" data-testid="wagb-error-panel">
      <div className="flex items-start gap-1.5 font-semibold mb-1">
        <AlertTriangle size={14} className="mt-0.5" />
        <span className="break-all">{title}</span>
      </div>
      {message && (
        <pre className="whitespace-pre-wrap font-mono text-[11px] mt-1 max-h-40 overflow-auto">{message}</pre>
      )}
      {details && (
        <details className="mt-2">
          <summary className="cursor-pointer">Diagnostics</summary>
          <pre className="whitespace-pre-wrap font-mono text-[11px] mt-1 max-h-64 overflow-auto bg-white/70 p-2 rounded">
            {JSON.stringify(details, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function DeliveryRow({ r }) {
  const [open, setOpen] = useState(false);
  const [shot, setShot] = useState(null);   // blob URL for the screenshot
  const [enlarged, setEnlarged] = useState(false);
  const [logs, setLogs] = useState(null);
  const loadShot = async () => {
    if (shot || !r.screenshot_path) return;
    try {
      const rr = await api.get(`/broadcasts/jobs/${r.id}/screenshot`, { responseType: 'blob' });
      setShot(URL.createObjectURL(rr.data));
    } catch { /* screenshot may have been cleaned up */ }
  };
  const loadLogs = async () => {
    if (logs) return;
    try {
      const rr = await api.get(`/broadcasts/jobs/${r.id}/logs`);
      setLogs(rr.data?.data || []);
    } catch { /* logs endpoint 404 for very old jobs */ }
  };
  const toggle = () => {
    const next = !open; setOpen(next);
    if (next) { loadShot(); loadLogs(); }
  };
  return (
    <>
      <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`wagb-job-${r.id}`}>
        <td className="px-2 py-2">
          <button onClick={toggle} className="text-gray-500 hover:text-gray-800" data-testid={`wagb-job-${r.id}-toggle`}>
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="px-3 py-2 font-medium">{r.crm_hotel_name}</td>
        <td className="px-3 py-2">{r.gsheet_tab_name || <span className="text-gray-400 italic">—</span>}</td>
        <td className="px-3 py-2">{r.wa_group_name}</td>
        <td className="px-3 py-2 font-mono text-xs">
          <div>{r.report_date}</div>
          {r.send_date && r.send_date !== r.report_date && (
            <div className="text-[10px] text-gray-500 mt-0.5">↳ sent {r.send_date}</div>
          )}
        </td>
        <td className="px-3 py-2">
          <StatusBadge s={r.status} />
          {r.friendly_error && (r.status === 'failed' || r.status === 'blocked' || r.status === 'skipped') && (
            <div className="text-[11px] text-red-700 mt-1 max-w-[200px]" data-testid={`wagb-job-${r.id}-friendly-error`} title={r.friendly_error}>
              {r.friendly_error}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-center text-xs">
          {r.broadcast_type === 'manual' ? (
            <span className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-medium" data-testid={`wagb-job-${r.id}-btype`}>Manual</span>
          ) : r.delivery_type === 'text_only' ? (
            <span className="inline-block px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 font-medium" data-testid={`wagb-job-${r.id}-btype`}>Text Only</span>
          ) : r.delivery_type === 'video' ? (
            <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium" data-testid={`wagb-job-${r.id}-btype`}>Video</span>
          ) : r.delivery_type === 'image' ? (
            <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium" data-testid={`wagb-job-${r.id}-btype`}>CompSet</span>
          ) : '—'}
        </td>
        <td className="px-3 py-2 text-center">{r.attempts}/{r.max_attempts}</td>
        <td className="px-3 py-2 font-mono text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
        <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate" title={r.friendly_error || r.skip_reason || r.wa_error}>
          {r.friendly_error || r.skip_reason || r.wa_error || '—'}
        </td>
        <td className="px-3 py-2 text-center">
          {r.screenshot_path ? (
            <button onClick={toggle} className="text-teal-600 hover:text-teal-800" data-testid={`wagb-job-${r.id}-screenshot-btn`}>
              <Eye size={14} className="inline" />
            </button>
          ) : '—'}
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50 border-b border-gray-200" data-testid={`wagb-job-${r.id}-expanded`}>
          <td colSpan="11" className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-3">
                {(r.skip_reason || r.wa_error) && (
                  <ErrorPanel raw={r.skip_reason || r.wa_error} />
                )}
                <div>
                  <div className="text-xs font-semibold text-gray-700 mb-1">
                    {r.broadcast_type === 'manual'
                      ? 'Manual Announcement message'
                      : r.delivery_type === 'text_only'
                        ? 'Text message sent (no screenshot)'
                        : 'Rendered message (what was sent)'}
                  </div>
                  {r.rendered_caption ? (
                    <pre className="whitespace-pre-wrap text-sm bg-white p-3 rounded border border-gray-200 font-sans" data-testid={`wagb-job-${r.id}-caption`}>
                      {r.rendered_caption}
                    </pre>
                  ) : (
                    <div className="text-xs text-gray-500 italic bg-white p-3 rounded border border-gray-200">
                      {r.broadcast_type === 'manual'
                        ? 'Image-only announcement — no caption provided.'
                        : r.delivery_type === 'text_only'
                          ? 'Text-only delivery — no CompSet data was available for this Report Date.'
                          : 'No caption stored — job did not reach the sending phase (e.g. Bridge not configured or screenshot missing).'}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500">WhatsApp Message ID</div>
                    <div className="font-mono break-all" data-testid={`wagb-job-${r.id}-message-id`}>
                      {r.bridge_message_id || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Retry Count</div>
                    <div>{r.attempts} / {r.max_attempts}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Group ID</div>
                    <div className="font-mono break-all">{r.wa_group_id || '—'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Job ID</div>
                    <div className="font-mono break-all">{r.id}</div>
                  </div>
                </div>
                {logs && logs.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-700 font-semibold">Audit trail ({logs.length} events)</summary>
                    <div className="mt-2 max-h-48 overflow-auto bg-white rounded border border-gray-200 divide-y divide-gray-100">
                      {logs.map((l, i) => (
                        <div key={i} className="px-2 py-1 flex gap-2">
                          <span className="font-mono text-gray-500">{new Date(l.timestamp).toLocaleTimeString()}</span>
                          <span className="font-semibold text-gray-800">{l.event}</span>
                          {l.detail && Object.keys(l.detail).length > 0 && (
                            <span className="text-gray-500 truncate">{JSON.stringify(l.detail)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-1">
                  {r.broadcast_type === 'manual'
                    ? (r.delivery_type === 'video' ? 'Announcement video' : 'Announcement image')
                    : 'Screenshot preview'}
                </div>
                {shot ? (
                  r.delivery_type === 'video' ? (
                    <video
                      src={shot}
                      controls
                      className="max-w-full rounded border border-gray-200"
                      data-testid={`wagb-job-${r.id}-video-thumbnail`}
                    />
                  ) : (
                    <button onClick={() => setEnlarged(true)} className="block w-full">
                      <img src={shot} alt="screenshot" className="max-w-full rounded border border-gray-200 cursor-zoom-in hover:opacity-90"
                           data-testid={`wagb-job-${r.id}-thumbnail`} />
                    </button>
                  )
                ) : r.delivery_type === 'text_only' ? (
                  <div className="text-xs text-gray-500 italic bg-white p-6 rounded border border-gray-200 text-center">
                    No screenshot — text-only broadcast.
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic bg-white p-6 rounded border border-gray-200 text-center">
                    {r.screenshot_path ? <Loader2 className="animate-spin inline" size={14} /> : 'No screenshot generated.'}
                  </div>
                )}
              </div>
            </div>
            {enlarged && shot && (
              <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-6" onClick={() => setEnlarged(false)}>
                <img src={shot} alt="screenshot enlarged" className="max-w-full max-h-full" />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DelayPicker({ value, onChange }) {
  const presets = [5, 10, 15, 20, 30];
  const isCustom = !presets.includes(Number(value));
  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="text-xs font-semibold text-gray-700 mb-2">Delay Between Groups</div>
      <div className="flex flex-wrap items-center gap-4">
        {presets.map(n => (
          <label key={n} className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input
              type="radio"
              checked={Number(value) === n && !isCustom}
              onChange={() => onChange(n)}
              data-testid={`wagb-delay-${n}`}
            />
            {n}s
          </label>
        ))}
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input
            type="radio"
            checked={isCustom}
            onChange={() => onChange(45)}
            data-testid="wagb-delay-custom"
          />
          Custom
        </label>
        {isCustom && (
          <input
            type="number"
            min="0"
            max="600"
            className="input !w-24 !py-1"
            value={value}
            onChange={e => onChange(Math.max(0, Number(e.target.value || 0)))}
            data-testid="wagb-delay-custom-value"
          />
        )}
        <span className="text-xs text-gray-500 ml-auto">
          e.g. 10s → 11:00:00 → G1, 11:00:10 → G2, 11:00:20 → G3
        </span>
      </div>
    </div>
  );
}

function BroadcastTemplateCard({ s, setS, save, saving }) {
  // Iter238 — two templates:
  //  • caption_template            → CompSet mappings (screenshot + caption)
  //  • caption_template_non_compset → Text-Only mappings (Non-CompSet, no screenshot)
  const [which, setWhich] = useState('compset');   // 'compset' | 'non_compset'
  const [previewing, setPreviewing] = useState(false);
  const [rendered, setRendered] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [previewMappingId, setPreviewMappingId] = useState('');
  const currentKey = which === 'compset' ? 'caption_template' : 'caption_template_non_compset';
  const currentVal = s[currentKey] || '';
  useEffect(() => {
    api.get('/broadcasts/mappings').then(r => setMappings(r.data?.data || [])).catch(() => {});
  }, []);
  const preview = async () => {
    setPreviewing(true);
    try {
      const body = { template: currentVal };
      if (previewMappingId) body.mapping_id = previewMappingId;
      const r = await api.post('/broadcasts/preview-caption', body);
      setRendered(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed.'); }
    finally { setPreviewing(false); }
  };
  return (
    <Card title="Broadcast Message Template" icon={<MessageSquare size={16} />}>
      <div className="flex gap-1 border-b border-gray-200 mb-3" data-testid="wagb-template-tabs">
        {[
          { id: 'compset', label: 'CompSet Template' },
          { id: 'non_compset', label: 'Non-CompSet Template' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => { setWhich(t.id); setRendered(null); }}
            className={`px-3 py-1.5 text-xs font-medium ${which === t.id ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-800'}`}
            data-testid={`wagb-template-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-600 mb-2">
        {which === 'compset'
          ? <>Sent with the CompSet screenshot for mappings that have a Google Sheet worksheet. Supports <code>{'{{hotel_name}}'}</code>, <code>{'{{sheet_name}}'}</code>, <code>{'{{date}}'}</code>.</>
          : <>Sent as a plain-text message for mappings without a Google Sheet worksheet. Supports <code>{'{{hotel_name}}'}</code>, <code>{'{{date}}'}</code> (<code>{'{{sheet_name}}'}</code> resolves to empty).</>
        }
      </p>
      <textarea
        className="input font-mono text-xs"
        rows={7}
        value={currentVal}
        onChange={e => setS(v => ({ ...v, [currentKey]: e.target.value }))}
        placeholder={which === 'compset'
          ? 'Dear {{hotel_name}}, Please find your Compset Report for {{date}}.'
          : 'Dear {{hotel_name}},\n\nPlease find today\'s update for {{date}}.\nKindly check and update your room rates accordingly.\n\nThank You,\nSuperhost Team'}
        data-testid={`wagb-template-textarea-${which}`}
      />
      <div className="flex flex-wrap items-end gap-2 mt-3">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="wagb-save-template">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Template
        </button>
        <div className="flex items-end gap-1">
          <div>
            <div className="text-xs text-gray-500 mb-0.5">Preview using mapping (optional)</div>
            <select
              className="input !py-1 text-xs"
              value={previewMappingId}
              onChange={e => setPreviewMappingId(e.target.value)}
              data-testid="wagb-template-preview-mapping"
            >
              <option value="">— use dummy values —</option>
              {mappings.map(m => (
                <option key={m.id} value={m.id}>
                  {m.crm_hotel_name} · {m.gsheet_tab_name || '(no worksheet)'}
                </option>
              ))}
            </select>
          </div>
          <button onClick={preview} disabled={previewing} className="btn-secondary" data-testid="wagb-template-preview">
            {previewing ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />} Preview
          </button>
        </div>
      </div>
      {rendered && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded" data-testid="wagb-template-preview-out">
          <div className="text-xs font-semibold text-emerald-800 mb-1">Rendered message that will be sent:</div>
          <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{rendered.rendered}</pre>
          <div className="text-xs text-emerald-700 mt-2">
            {which === 'compset'
              ? 'Screenshot will be attached below.'
              : 'This is a text-only message — no screenshot will be attached.'}
            <span className="ml-2 opacity-70">
              Vars: hotel_name=&quot;{rendered.variables?.hotel_name || '—'}&quot; ·
              sheet_name=&quot;{rendered.variables?.sheet_name || '—'}&quot; ·
              date=&quot;{rendered.variables?.date || '—'}&quot;
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

function AnnouncementPanel({ n, groups, onLoadGroups, groupsLoading, state, setState }) {
  // Iter244 — one Announcement (1 or 2). All local UI concerns live inside
  // this panel; the parent AnnouncementTab owns the shared groups list and
  // orchestrates the send.
  const [term, setTerm] = useState('');
  const filtered = term
    ? groups.filter(g => (g.name || '').toLowerCase().includes(term.toLowerCase())).slice(0, 500)
    : groups.slice(0, 500);
  const set = (patch) => setState(prev => ({ ...prev, ...patch }));
  const onImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB).'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      // iter280 — clear any staged video (image and video are mutually exclusive).
      set({ imgB64: b64, imgName: file.name, imgPreview: dataUrl,
            videoB64: '', videoName: '', videoPreview: '', videoMime: '' });
    };
    reader.readAsDataURL(file);
  };
  const clearImage = () => set({ imgB64: '', imgName: '', imgPreview: '' });
  // iter280 — Video upload. Accepted formats: MP4, MOV, AVI. Max 50MB.
  const VIDEO_MIME_BY_EXT = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
  };
  const onVideoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!VIDEO_MIME_BY_EXT[ext]) {
      toast.error('Unsupported video format. Use MP4, MOV or AVI.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video too large (max 50MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      const mime = file.type || VIDEO_MIME_BY_EXT[ext];
      // Clear any staged image so image/video stay mutually exclusive.
      set({ videoB64: b64, videoName: file.name, videoPreview: dataUrl, videoMime: mime,
            imgB64: '', imgName: '', imgPreview: '' });
    };
    reader.readAsDataURL(file);
  };
  const clearVideo = () => set({ videoB64: '', videoName: '', videoPreview: '', videoMime: '' });
  const toggleGroup = (g) => {
    set({
      selected: state.selected.find(x => x.id === g.id)
        ? state.selected.filter(x => x.id !== g.id)
        : [...state.selected, { id: g.id, name: g.name }],
    });
  };
  const selectAll = () => set({ selected: groups.map(g => ({ id: g.id, name: g.name })) });
  const clearAll = () => set({ selected: [] });
  const allSelected = groups.length > 0 && state.selected.length === groups.length;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`wagb-ann-${n}-panel`}>
      <div>
        <Field label="Upload Image (optional)">
          {state.imgPreview ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <img src={state.imgPreview} alt="preview" className="max-h-56 mx-auto rounded" data-testid={`wagb-ann-${n}-image-preview`} />
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 truncate">{state.imgName}</span>
                <button type="button" onClick={clearImage} className="text-red-600 hover:text-red-800" data-testid={`wagb-ann-${n}-image-remove`}>Remove</button>
              </div>
            </div>
          ) : (
            <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${state.videoB64 ? 'border-gray-100 bg-gray-50 opacity-50 pointer-events-none' : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/40'}`}>
              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={onImageChange} data-testid={`wagb-ann-${n}-image-input`} disabled={!!state.videoB64} />
              <span className="text-sm text-gray-500">Choose Image (JPG/PNG/WEBP, max 10MB)</span>
            </label>
          )}
        </Field>
        {/* iter280 — Video upload. Accepted formats: MP4, MOV, AVI. Max 50 MB. */}
        <Field label="Upload Video (optional)">
          {state.videoPreview ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`wagb-ann-${n}-video-preview-wrap`}>
              <video src={state.videoPreview} className="max-h-56 mx-auto rounded" controls data-testid={`wagb-ann-${n}-video-preview`} />
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 truncate">{state.videoName}</span>
                <button type="button" onClick={clearVideo} className="text-red-600 hover:text-red-800" data-testid={`wagb-ann-${n}-video-remove`}>Remove</button>
              </div>
            </div>
          ) : (
            <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${state.imgB64 ? 'border-gray-100 bg-gray-50 opacity-50 pointer-events-none' : 'border-gray-200 hover:border-sky-400 hover:bg-sky-50/40'}`}>
              <input type="file" accept=".mp4,.mov,.avi,video/mp4,video/quicktime,video/x-msvideo" className="hidden" onChange={onVideoChange} data-testid={`wagb-ann-${n}-video-input`} disabled={!!state.imgB64} />
              <span className="text-sm text-gray-500">Choose Video (MP4/MOV/AVI, max 50MB)</span>
            </label>
          )}
          {state.imgB64 && !state.videoB64 && (
            <p className="text-[11px] text-gray-400 mt-1">Remove the image to attach a video instead.</p>
          )}
        </Field>
        <Field label="Announcement Message">
          <textarea
            className="input font-sans text-sm"
            rows={7}
            value={state.text}
            onChange={e => set({ text: e.target.value })}
            placeholder="Dear Hotel Partner, ..."
            data-testid={`wagb-ann-${n}-text`}
          />
          <p className="text-xs text-gray-500 mt-1">
            An image, a video, a message, or a mix — image and video are mutually exclusive.
          </p>
        </Field>
      </div>
      <div>
        <Field label={`Select WhatsApp Groups (${state.selected.length} selected)`}>
          <div className="flex gap-1 mb-2">
            <input
              className="input flex-1"
              placeholder={groups.length ? `Search groups (${groups.length})…` : 'No groups loaded'}
              value={term}
              onChange={e => setTerm(e.target.value)}
              data-testid={`wagb-ann-${n}-group-search`}
            />
            <button type="button" onClick={onLoadGroups} className="btn-secondary !px-2" title="Refresh groups" data-testid={`wagb-ann-${n}-refresh-groups`}>
              {groupsLoading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
            </button>
          </div>
          {/* Iter244 — Select All / Clear All. Select All picks EVERY loaded
              group (not just the currently filtered slice); after that the
              user can uncheck individual groups even from within a search. */}
          <div className="flex items-center gap-2 mb-2 text-xs">
            <button
              type="button"
              onClick={selectAll}
              disabled={groups.length === 0 || allSelected}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`wagb-ann-${n}-select-all`}
            >
              <CheckCircle2 size={12} /> Select All Groups
              {groups.length > 0 && <span className="opacity-70">({groups.length})</span>}
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={state.selected.length === 0}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`wagb-ann-${n}-clear-all`}
            >
              <Trash2 size={12} /> Clear All
            </button>
          </div>
          {state.selected.length > 0 && state.selected.length <= 12 && (
            <div className="flex flex-wrap gap-1 mb-2" data-testid={`wagb-ann-${n}-selected-chips`}>
              {state.selected.map(g => (
                <span key={g.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 rounded-full">
                  {g.name}
                  <button type="button" onClick={() => toggleGroup(g)} className="ml-1 hover:text-emerald-950">×</button>
                </span>
              ))}
            </div>
          )}
          <div className="max-h-72 overflow-auto border border-gray-200 rounded-lg" data-testid={`wagb-ann-${n}-group-list`}>
            {groups.length === 0 ? (
              <p className="text-xs text-amber-700 p-3">
                No groups loaded. Make sure the Bridge state is <code>ready</code> (Setup tab), then click refresh.
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-gray-500 p-3">No matches for &ldquo;{term}&rdquo;.</p>
            ) : filtered.map(g => {
              const checked = !!state.selected.find(s => s.id === g.id);
              return (
                <label
                  key={g.id}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-b border-gray-100 last:border-0 hover:bg-emerald-50 ${checked ? 'bg-emerald-50' : ''}`}
                  data-testid={`wagb-ann-${n}-group-${g.id}`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleGroup(g)} />
                  <span className="font-medium text-gray-900 truncate">{g.name}</span>
                </label>
              );
            })}
          </div>
        </Field>
      </div>
    </div>
  );
}


function AnnouncementTab() {
  // Iter238 — Manual Announcement. Iter244 — optional second announcement
  // sent after a configurable delay. Both share the loaded WA groups list
  // but each has its OWN independent selection.
  // iter280 — added video* fields (video is mutually exclusive with image).
  const emptyState = { text: '', imgB64: '', imgName: '', imgPreview: '',
                       videoB64: '', videoName: '', videoPreview: '', videoMime: '',
                       selected: [] };
  const [ann1, setAnn1] = useState(emptyState);
  const [ann2, setAnn2] = useState({ ...emptyState });
  const [showSecond, setShowSecond] = useState(false);
  const [delay, setDelay] = useState(15);   // seconds between Ann 1 and Ann 2
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // iter254 — Optional Poll attached to Announcement 1. The poll is sent
  // to EXACTLY the same groups selected in Announcement 1 (spec item 2).
  // Default options: Interested / Not Interested — both editable.
  const [addPoll, setAddPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('Are you interested in the Superhost App update?');
  const [pollOptions, setPollOptions] = useState(['Interested', 'Not Interested']);

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const r = await api.get('/broadcasts/wa/groups');
      if (r.data?.ok) setGroups(r.data.data || []);
      else if (r.data?.error) toast.error(`WA groups: ${r.data.error}`);
    } catch { /* Bridge may not be configured yet — silent. */ }
    finally { setGroupsLoading(false); }
  };
  useEffect(() => { loadGroups(); }, []);

  const validate = (a, label) => {
    if (!a.text.trim() && !a.imgB64 && !a.videoB64) { toast.error(`${label}: add a message, an image, a video, or a mix.`); return false; }
    if (a.imgB64 && a.videoB64) { toast.error(`${label}: pick either an image OR a video, not both.`); return false; }
    if (a.selected.length === 0) { toast.error(`${label}: select at least one WhatsApp group.`); return false; }
    return true;
  };
  const validatePoll = () => {
    if (!addPoll) return true;
    if (!pollQuestion.trim()) { toast.error('Poll: question is required.'); return false; }
    const opts = pollOptions.map(o => o.trim()).filter(Boolean);
    if (opts.length < 2) { toast.error('Poll: at least 2 non-empty options required.'); return false; }
    return true;
  };
  const send = async () => {
    // iter254 — Poll-only case: no announcement text/image/video needed.
    if (addPoll && !ann1.text.trim() && !ann1.imgB64 && !ann1.videoB64) {
      // Skip the "text or media required" check but still require a group.
      if (ann1.selected.length === 0) { toast.error('Poll: select at least one WhatsApp group.'); return; }
    } else if (!validate(ann1, 'Announcement 1')) {
      return;
    }
    if (!validatePoll()) return;
    // Send announcement path (if any content). Poll follows separately.
    const items = [];
    if (ann1.text.trim() || ann1.imgB64 || ann1.videoB64) {
      items.push({
        text: ann1.text.trim(),
        image_base64: ann1.imgB64 || null,
        image_filename: ann1.imgName || null,
        video_base64: ann1.videoB64 || null,
        video_filename: ann1.videoName || null,
        video_mimetype: ann1.videoMime || null,
        wa_groups: ann1.selected,
        delay_after_previous_seconds: 0,
      });
    }
    if (showSecond) {
      if (!validate(ann2, 'Announcement 2')) return;
      const d = Number(delay);
      if (!Number.isFinite(d) || d < 0 || d > 3600) {
        toast.error('Delay must be between 0 and 3600 seconds.');
        return;
      }
      items.push({
        text: ann2.text.trim(),
        image_base64: ann2.imgB64 || null,
        image_filename: ann2.imgName || null,
        video_base64: ann2.videoB64 || null,
        video_filename: ann2.videoName || null,
        video_mimetype: ann2.videoMime || null,
        wa_groups: ann2.selected,
        delay_after_previous_seconds: d,
      });
    }
    setSending(true); setResult(null);
    try {
      let annResult = null;
      if (items.length > 0) {
        const r = await api.post('/broadcasts/manual/send', { announcements: items });
        annResult = r.data;
      }
      let pollResult = null;
      if (addPoll) {
        // Send poll AFTER announcement (so it arrives second in the chat).
        const r2 = await api.post('/broadcasts/polls', {
          announcement_id: annResult?.announcements?.[0]?.announcement_id || null,
          announcement_text: null, // announcement already sent above (if any)
          question: pollQuestion.trim(),
          options: pollOptions.map(o => o.trim()).filter(Boolean),
          wa_groups: ann1.selected,
          allow_multiple_answers: false,
        });
        pollResult = r2.data;
      }
      setResult({ announcement: annResult, poll: pollResult });
      const queued = (annResult?.queued || 0);
      const pollSent = (pollResult?.sent || 0);
      if (addPoll) {
        toast.success(`Queued ${queued} announcement send(s) + ${pollSent}/${pollResult?.total_groups || 0} poll(s).`);
      } else {
        toast.success(`Queued ${queued} send(s). Watch Live Progress + Deliveries.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
    } finally { setSending(false); }
  };
  const removeSecond = () => {
    setShowSecond(false);
    setAnn2({ text: '', imgB64: '', imgName: '', imgPreview: '',
              videoB64: '', videoName: '', videoPreview: '', videoMime: '',
              selected: [] });
  };

  return (
    <div className="space-y-6" data-testid="wagb-announcement-tab">
      <Card title="Manual Announcement" icon={<MessageSquare size={16} />}>
        <p className="text-xs text-gray-600 mb-4">
          Send one or two announcements directly to selected WhatsApp groups.
          Independent of Google Sheets, mappings and the daily scheduler.
        </p>
        <div className="mb-3 pb-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Announcement 1</h3>
          <AnnouncementPanel n={1} groups={groups} onLoadGroups={loadGroups}
            groupsLoading={groupsLoading} state={ann1} setState={setAnn1} />
        </div>
        {showSecond ? (
          <div className="mb-3 pb-3 border-b border-gray-100 bg-sky-50/30 rounded-lg -mx-2 px-2 py-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Announcement 2</h3>
              <button
                type="button"
                onClick={removeSecond}
                className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                data-testid="wagb-ann-remove-second"
              >
                <Trash2 size={12} /> Remove Second Announcement
              </button>
            </div>
            <div className="flex items-center gap-2 mb-3 text-sm">
              <label className="text-gray-700">Delay after Announcement 1:</label>
              <select
                value={[5,10,15,20,30].includes(Number(delay)) ? String(delay) : 'custom'}
                onChange={e => e.target.value !== 'custom' && setDelay(Number(e.target.value))}
                className="input !py-1 !text-sm w-28"
                data-testid="wagb-ann-delay-preset"
              >
                <option value="5">5s</option>
                <option value="10">10s</option>
                <option value="15">15s</option>
                <option value="20">20s</option>
                <option value="30">30s</option>
                <option value="custom">Custom</option>
              </select>
              <input
                type="number"
                min="0"
                max="3600"
                value={delay}
                onChange={e => setDelay(e.target.value)}
                className="input !py-1 !text-sm w-24"
                data-testid="wagb-ann-delay-custom"
              />
              <span className="text-xs text-gray-500">seconds</span>
            </div>
            <AnnouncementPanel n={2} groups={groups} onLoadGroups={loadGroups}
              groupsLoading={groupsLoading} state={ann2} setState={setAnn2} />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowSecond(true)}
            className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-dashed border-gray-300 text-gray-600 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50"
            data-testid="wagb-ann-add-second"
          >
            <PlusCircle size={14} /> Add Second Announcement
          </button>
        )}

        {/* iter254 — Add Poll toggle. When ON, the poll is sent to the same
            groups selected in Announcement 1. When OFF, this whole block is
            hidden and the existing announcement flow behaves EXACTLY as
            before (no payload changes, no extra API calls). */}
        <div className="mt-2 mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
            <input
              type="checkbox"
              checked={addPoll}
              onChange={e => setAddPoll(e.target.checked)}
              data-testid="wagb-poll-toggle"
              className="rounded"
            />
            Add Poll
            {addPoll && (
              <span className="ml-2 text-xs text-gray-500 font-normal">
                Sent to the same groups as Announcement 1 ({ann1.selected.length} group{ann1.selected.length === 1 ? '' : 's'})
              </span>
            )}
          </label>
          {addPoll && (
            <div className="mt-3 space-y-2" data-testid="wagb-poll-editor">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Poll Question</label>
                <input
                  value={pollQuestion}
                  onChange={e => setPollQuestion(e.target.value)}
                  className="input !py-1.5 !text-sm w-full"
                  placeholder="e.g., Are you interested in the Superhost App update?"
                  data-testid="wagb-poll-question"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Poll Options</label>
                {pollOptions.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs text-gray-500 w-6">#{i + 1}</span>
                    <input
                      value={opt}
                      onChange={e => setPollOptions(pollOptions.map((o, idx) => idx === i ? e.target.value : o))}
                      className="input !py-1 !text-sm flex-1"
                      data-testid={`wagb-poll-option-${i}`}
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setPollOptions(pollOptions.filter((_, idx) => idx !== i))}
                        className="text-red-500 hover:text-red-700 text-xs"
                        data-testid={`wagb-poll-option-remove-${i}`}
                      >Remove</button>
                    )}
                  </div>
                ))}
                {pollOptions.length < 12 && (
                  <button
                    type="button"
                    onClick={() => setPollOptions([...pollOptions, ''])}
                    className="mt-1 text-xs text-emerald-700 hover:underline"
                    data-testid="wagb-poll-add-option"
                  >+ Add option</button>
                )}
              </div>
              <p className="text-[11px] text-gray-500">WhatsApp limits polls to 12 options.</p>
            </div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={send}
            disabled={sending}
            className="btn-primary"
            data-testid="wagb-ann-send"
          >
            {sending ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
            {showSecond ? ' Send Announcement 1 + 2' : (addPoll ? ' Send Announcement + Poll' : ' Send Announcement')}
          </button>
          {result?.announcement && (
            <span className="text-xs text-emerald-700" data-testid="wagb-ann-result">
              Queued <strong>{result.announcement.queued}</strong> send(s)
              {result.poll && (<>{' '}+ <strong>{result.poll.sent}/{result.poll.total_groups}</strong> poll(s)</>)}
            </span>
          )}
          {!result?.announcement && result?.poll && (
            <span className="text-xs text-emerald-700" data-testid="wagb-poll-result">
              Poll sent to <strong>{result.poll.sent}/{result.poll.total_groups}</strong> group(s)
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}


function SearchableCombobox({ items, value, matchBy = 'id', placeholder, emptyLabel, testIdPrefix, onChange }) {
  // Iter235 — same UX pattern as PropertyCombobox but for plain [{id, name}]
  // lists (Google Sheet worksheets + WhatsApp groups). Case-insensitive
  // partial-name filter, no auto-select, click-outside closes, capped at
  // 200 rendered rows to keep the DOM light with 500+ groups.
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const ref = useRef(null);
  const selected = items.find(i => String(i[matchBy]) === String(value));
  const filtered = term
    ? items.filter(i => (i.name || '').toLowerCase().includes(term.toLowerCase())).slice(0, 200)
    : items.slice(0, 200);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <input
        className="input w-full"
        placeholder={placeholder || 'Search…'}
        value={open ? term : (selected?.name || '')}
        onFocus={() => { setOpen(true); setTerm(''); }}
        onChange={e => { setTerm(e.target.value); setOpen(true); }}
        data-testid={`${testIdPrefix}-search`}
      />
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg" data-testid={`${testIdPrefix}-list`}>
          {items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyLabel || 'No items available'}</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No matches for &ldquo;{term}&rdquo;</div>
          ) : filtered.map(i => (
            <button
              key={String(i.id) + '::' + i.name}
              type="button"
              onClick={() => { onChange(i); setOpen(false); setTerm(''); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-gray-100 last:border-0 ${String(i[matchBy]) === String(value) ? 'bg-emerald-50' : ''}`}
              data-testid={`${testIdPrefix}-opt-${i.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-gray-900 truncate">{i.name}</div>
                {/* iter331 — WA groups surface the connected number's
                    role via `is_admin`. Rendered as a tiny pill so the
                    operator can tell at a glance whether this group
                    will accept broadcasts without needing admin
                    privileges. Non-group items (e.g. worksheet names)
                    don't carry `is_admin` and the pill is suppressed. */}
                {typeof i.is_admin === 'boolean' && (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${i.is_admin ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}
                    data-testid={`${testIdPrefix}-opt-${i.id}-role`}
                  >
                    {i.is_admin ? 'Admin' : 'Member'}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function PropertyCombobox({ properties, loading, value, onChange, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const ref = useRef(null);
  const selected = properties.find(p => p.id === value);
  // iter332 — No artificial cap. The dropdown must cover the complete
  // unmapped-property dataset (hundreds / thousands of rows). The DOM
  // handles 2k+ small buttons fine; we expose an infinite-scroll
  // sentinel via `max-h-72 overflow-y-auto` on the panel so scroll
  // works end to end. Search still narrows aggressively.
  const filtered = term
    ? properties.filter(p => {
        const s = term.toLowerCase();
        return (p.name || '').toLowerCase().includes(s) || (p.grb || '').toLowerCase().includes(s);
      })
    : properties;
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="flex gap-1">
        <input
          className="input flex-1"
          placeholder={loading ? 'Loading properties…' : 'Search by name or GRB ID…'}
          value={open ? term : (selected?.name || '')}
          onFocus={() => { setOpen(true); setTerm(''); }}
          onChange={e => { setTerm(e.target.value); setOpen(true); }}
          data-testid="wagb-map-hotel-search"
        />
        <button type="button" onClick={onRefresh} className="btn-secondary !px-2" title="Reload properties" data-testid="wagb-map-refresh-props">
          {loading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
        </button>
      </div>
      {selected && !open && (
        <p className="text-xs text-gray-500 mt-1 truncate" data-testid="wagb-map-hotel-selected">
          GRB: <span className="font-mono">{selected.grb || '—'}</span>
          {selected.city ? <> · {selected.city}</> : null}
        </p>
      )}
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg" data-testid="wagb-map-hotel-list">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No properties match &ldquo;{term}&rdquo;</div>
          ) : filtered.map(p => {
            // iter332 — Preserve the EXACT status text from the CRM
            // record. Active / Inactive / Discontinued / Terminated
            // and any other valid status pass through literally. The
            // badge colour is picked from a lookup; unknown values
            // fall back to a neutral grey so the dropdown never
            // crashes on a new status term.
            const sRaw = (p.status || 'Active').toString();
            const sLower = sRaw.toLowerCase();
            const sCls =
              sLower === 'active'       ? 'bg-emerald-100 text-emerald-700' :
              sLower === 'inactive'     ? 'bg-amber-100 text-amber-700' :
              sLower === 'discontinued' ? 'bg-red-100 text-red-700' :
              sLower === 'terminated'   ? 'bg-rose-100 text-rose-700' :
              /* fallback */              'bg-gray-100 text-gray-600';
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p); setOpen(false); setTerm(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-gray-100 last:border-0 ${p.id === value ? 'bg-emerald-50' : ''}`}
                data-testid={`wagb-map-hotel-opt-${p.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-gray-900 truncate">{p.name}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${sCls}`}
                        data-testid={`wagb-map-hotel-opt-${p.id}-status`}>
                    {sRaw}
                  </span>
                </div>
                <div className="text-xs text-gray-500 flex gap-2">
                  {p.grb ? <span className="font-mono">GRB: {p.grb}</span> : null}
                  {p.city ? <span>· {p.city}</span> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const StatusBadge = ({ s }) => {
  const map = {
    pending: 'bg-gray-100 text-gray-700',
    screenshotting: 'bg-blue-50 text-blue-700',
    screenshot_ok: 'bg-cyan-50 text-cyan-700',
    sending: 'bg-indigo-50 text-indigo-700',
    sent: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
    skipped: 'bg-amber-50 text-amber-700',
    blocked: 'bg-orange-50 text-orange-800 border border-orange-200',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[s] || 'bg-gray-100 text-gray-700'}`}>{s}</span>;
};

const Card = ({ title, icon, right, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5">
    <div className="flex items-start justify-between mb-3 gap-2">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">{icon}{title}</h3>
      {right}
    </div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);
// Tailwind form styles reused across the module.
const style = document.createElement('style');
style.innerHTML = `
  .input { display:block; width:100%; padding:8px 10px; font-size:14px; border:1px solid #d1d5db; border-radius:8px; }
  .input:focus { outline: none; border-color:#059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.2); }
  .btn-primary { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; background:#059669; color:white; font-size:13px; font-weight:500; }
  .btn-primary:hover { background:#047857; }
  .btn-primary:disabled { opacity:0.6; }
  .btn-secondary { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; background:#f3f4f6; color:#374151; font-size:13px; font-weight:500; border:1px solid #e5e7eb; }
  .btn-secondary:hover { background:#e5e7eb; }
`;
if (typeof document !== 'undefined' && !document.getElementById('wagb-styles')) {
  style.id = 'wagb-styles';
  document.head.appendChild(style);
}

// ────────────────────────────────────────────────────────────────────
// iter254 — Poll Responses tab
// ────────────────────────────────────────────────────────────────────
function PollResponsesTab() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/broadcasts/polls');
      setPolls(r.data?.polls || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load polls.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="wagb-polls-tab">
      <div className="flex items-center gap-2">
        <BarChart3 size={16} className="text-emerald-600" />
        <h2 className="text-lg font-semibold text-gray-900">Poll Responses</h2>
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50"
          data-testid="wagb-polls-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      {loading && polls.length === 0 && (
        <p className="text-sm text-gray-500 flex items-center gap-1.5" data-testid="wagb-polls-loading">
          <Loader2 size={14} className="animate-spin" /> Loading polls…
        </p>
      )}
      {!loading && polls.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="wagb-polls-empty">
          No polls yet. Send an announcement with a poll from the Announcement tab.
        </p>
      )}
      <div className="space-y-3">
        {polls.map(p => (
          <div key={p.poll_id} className="border border-gray-200 rounded-xl bg-white" data-testid={`wagb-poll-row-${p.poll_id}`}>
            <button
              onClick={() => setOpenId(openId === p.poll_id ? null : p.poll_id)}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50"
              data-testid={`wagb-poll-open-${p.poll_id}`}
            >
              {openId === p.poll_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <div className="text-left flex-1">
                <div className="text-sm font-semibold text-gray-800">{p.question}</div>
                <div className="text-[11px] text-gray-500">Sent: {formatDateTime(p.sent_at || p.created_at) || '—'}</div>
              </div>
              <div className="hidden sm:grid grid-cols-5 gap-3 text-center text-xs">
                <MetricStat label="Total" val={p.total_groups} />
                <MetricStat label="Responded" val={p.groups_responded} cls="text-emerald-700" />
                <MetricStat label="No Response" val={p.no_response} cls="text-amber-700" />
                <MetricStat label="Individual" val={p.total_individual_responses} />
                <MetricStat label="Rate" val={`${p.response_rate_percent}%`} cls="text-blue-700" />
              </div>
            </button>
            {openId === p.poll_id && <PollDetail pollId={p.poll_id} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricStat({ label, val, cls = '' }) {
  return (
    <div>
      <div className="text-[10px] text-gray-500 uppercase">{label}</div>
      <div className={`text-sm font-semibold ${cls}`}>{val}</div>
    </div>
  );
}

function PollDetail({ pollId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');   // all | responded | <option_label> | no_response
  const [q, setQ] = useState('');
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/broadcasts/polls/${pollId}`);
      setData(r.data);
    } catch (e) { /* keep previous data on transient errors */ }
  };
  useEffect(() => {
    load();
    // iter254 — 5-second polling refresh while the drawer is open
    // (matches the existing broadcast_runs polling pattern; no WS).
    timerRef.current = setInterval(load, 5000);
    return () => clearInterval(timerRef.current);
  }, [pollId]);

  const download = () => {
    // Uses the axios wrapper's baseURL — token added by interceptor.
    api.get(`/broadcasts/polls/${pollId}/export.csv`, { responseType: 'blob' }).then(r => {
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `poll-${pollId}-responses.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(e => toast.error(e?.response?.data?.detail || 'CSV export failed.'));
  };

  // iter281 — force a Bridge → wwebjs → CRM re-sync of every vote on
  // this poll. Useful when the live vote_update stream missed events
  // (e.g. parentMessage uncached in the client Store).
  const [refreshing, setRefreshing] = useState(false);
  const refreshVotes = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/broadcasts/polls/${pollId}/refresh-votes`);
      const d = r.data || {};
      toast.success(`Refreshed: ${d.votes_upserted || 0} vote(s) synced from WhatsApp.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vote refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  if (!data) {
    return <div className="p-4 text-sm text-gray-500 flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  }

  const perGroup = data.per_group || [];
  const responses = data.responses || [];
  const options = data.poll?.options || [];

  // Combined table row: (group + individual response OR group + "no response")
  const rows = [];
  const byGroup = new Map(perGroup.map(g => [g.group_id, g]));
  const respByGroup = new Map();
  for (const r of responses) {
    if (!respByGroup.has(r.group_id)) respByGroup.set(r.group_id, []);
    respByGroup.get(r.group_id).push(r);
  }
  for (const g of perGroup) {
    const grs = respByGroup.get(g.group_id) || [];
    if (grs.length === 0) {
      rows.push({
        group_id: g.group_id,
        group_name: g.group_name,
        property_name: g.property_name || '',
        respondent_name: '',
        phone: '',
        response_label: 'No Response',
        responded_at: null,
      });
    } else {
      for (const r of grs) {
        rows.push({
          group_id: g.group_id,
          group_name: g.group_name,
          property_name: g.property_name || r.property_name || '',
          respondent_name: r.voter_name || '',
          phone: r.voter_phone || '',
          response_label: (r.selected_options || []).join(', ') || '—',
          responded_at: r.responded_at,
        });
      }
    }
  }

  const filtered = rows.filter(row => {
    if (filter === 'no_response' && row.response_label !== 'No Response') return false;
    if (filter !== 'all' && filter !== 'no_response') {
      if (row.response_label !== filter) return false;
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const bag = `${row.group_name} ${row.property_name} ${row.respondent_name} ${row.phone}`.toLowerCase();
      if (!bag.includes(s)) return false;
    }
    return true;
  });

  return (
    <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50" data-testid={`wagb-poll-detail-${pollId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`text-xs px-3 py-1 rounded-full border ${filter === 'all' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200'}`}
          data-testid="wagb-poll-filter-all"
        >All ({rows.length})</button>
        {options.map(o => (
          <button
            key={o}
            onClick={() => setFilter(o)}
            className={`text-xs px-3 py-1 rounded-full border ${filter === o ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200'}`}
            data-testid={`wagb-poll-filter-${o.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {o} ({data.summary.per_option_totals?.[o] || 0})
          </button>
        ))}
        <button
          onClick={() => setFilter('no_response')}
          className={`text-xs px-3 py-1 rounded-full border ${filter === 'no_response' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-200'}`}
          data-testid="wagb-poll-filter-no-response"
        >No Response ({data.summary.no_response})</button>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search group/property/name/phone"
              className="pl-6 pr-3 py-1 text-xs border border-gray-200 rounded-lg w-64"
              data-testid="wagb-poll-search"
            />
          </div>
          <button
            onClick={refreshVotes}
            disabled={refreshing}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
            data-testid="wagb-poll-refresh-votes"
            title="Re-sync every vote from WhatsApp — use if the live count looks off."
          >
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh votes
          </button>
          <button
            onClick={download}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
            data-testid="wagb-poll-export"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid={`wagb-poll-table-${pollId}`}>
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Group Name</th>
              <th className="px-3 py-2 text-left">Property Name</th>
              <th className="px-3 py-2 text-left">Respondent</th>
              <th className="px-3 py-2 text-left">Phone</th>
              <th className="px-3 py-2 text-left">Response</th>
              <th className="px-3 py-2 text-left">Response Date &amp; Time</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">No rows match the current filter.</td></tr>
            )}
            {filtered.map((row, i) => (
              <tr key={`${row.group_id}-${row.phone}-${i}`} className="border-t border-gray-100">
                <td className="px-3 py-2">{row.group_name || '—'}</td>
                <td className="px-3 py-2">{row.property_name || '—'}</td>
                <td className="px-3 py-2">{row.respondent_name || '—'}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{row.phone || '—'}</td>
                <td className="px-3 py-2">
                  <span className={
                    row.response_label === 'No Response' ? 'text-amber-700' :
                    row.response_label === 'Interested' ? 'text-emerald-700' :
                    row.response_label === 'Not Interested' ? 'text-red-700' : 'text-gray-800'
                  }>
                    {row.response_label}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.responded_at) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

